/**
 * Feedback Form (Phase 2 — Step 2.22).
 *
 * Simple local feedback storage — type, description, optional email.
 */

import React, { useState } from 'react';
import { useAppStore } from '@/store';
import { Button } from '../shared';
import { useToast } from '../shared';

interface FeedbackFormProps {
  onClose?: () => void;
}

export function FeedbackForm({ onClose }: FeedbackFormProps): React.ReactElement {
  const { addToast } = useToast();
  const store = useAppStore;

  const [type, setType] = useState<'bug' | 'feature' | 'general'>('general');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    store.getState().addFeedback({
      type,
      description: description.trim(),
      email: email.trim() || null,
    });

    addToast({ severity: 'success', message: 'Thank you for your feedback!' });
    setDescription('');
    setEmail('');
    onClose?.();
  };

  return (
    <div className="feedback-form" role="region" aria-label="Feedback">
      <h3>Send Feedback</h3>
      <form onSubmit={handleSubmit}>
        <label>
          Type
          <select value={type} onChange={(e) => setType(e.target.value as 'bug' | 'feature' | 'general')}>
            <option value="general">General</option>
            <option value="bug">Bug Report</option>
            <option value="feature">Feature Request</option>
          </select>
        </label>
        <label>
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your feedback..."
            rows={4}
            required
          />
        </label>
        <label>
          Email (optional)
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
          />
        </label>
        <div className="feedback-form__actions">
          <Button variant="primary" type="submit">Submit Feedback</Button>
          {onClose && <Button variant="secondary" onClick={onClose}>Cancel</Button>}
        </div>
      </form>
    </div>
  );
}
